/**
 * PatternPlayerNode - steps through a pattern, outputting events
 *
 * Endpoints:
 * - pattern (input): the pattern array to play
 * - next (input): advance to next step
 * - reset (input): reset to start
 * - index (input): jump to specific step
 * - event (output): event data at current step
 * - iteration (display): loop count, increments on wrap, resets on reset
 * - direction (input): 'forwards' or 'backwards'
 * - lengthRatio (input): duration multiplier
 */

import { BaseNode } from '@nodes/BaseNode';
import { Domain } from '@nodes/Endpoint';
import { events, params, triggers } from '@nodes/TypedEndpoints';
import type { Dependencies } from '@nodes/Dependencies';
import type { NodeDef } from '@runtime/IRuntime';
import type { Payload } from '@graph/Payload';
import type { IEndpoint } from '@runtime/IEndpoint';

export type Direction = 'forwards' | 'backwards';

const endpoints = {
  ...params({
    pattern: { order: 1, dataType: 'array', value: [60, 62, 64, 65] },
    iteration: { order: 3, value: 1, transient: true, step: 1, min: 1, max: 99 },
    index: { order: 2, transient: true, min: 0, max: 3, step: 1 },
    direction: { dataType: 'string', value: 'forwards', options: ['forwards', 'backwards'] },
    cropStart: {  },
    cropLength: {  },
    lengthRatio: { value: 0.8, min: 0.1, max: 2.0, step: 0.05 }
  }),
  ...events({
    event: { isOutput: true },
  }),
  ...triggers({
    next: { isInput: true },
    reset: {},
  })
};

export class PatternPlayerNode extends BaseNode<typeof endpoints> {
  private sourceNode: any = null;

  /** Debug: count of pattern updates (for testing) */
  patternSetCount = 0;

  constructor(id: string, deps: Dependencies) {
    super(id, deps);
  }

  initialize(): void {
    super.initialize();
    this.transport.on('start', this.boundReset);
  }

  dispose(): void {
    this.transport.off('start', this.boundReset);
    super.dispose();
  }

  private boundReset = () => this.resetIndex();

  get pattern(): number[] {
    return this.params.pattern.value;
  }

  /** Alias for test compatibility */
  get currentPattern(): number[] {
    return this.params.pattern.value;
  }

  handleTrigger(payload: Payload, endpointId: string, source: IEndpoint): void {
    if (endpointId === 'next') {
      this.next(payload);
    } else if (endpointId === 'reset') {
      this.reset(payload)
    };
  }

  onParamChanged(payload: Payload, endpointId: string): void {
    if (endpointId === 'pattern') {
      // Track pattern updates for debugging/testing
      this.patternSetCount++;
      // Update index endpoint metadata to reflect pattern length
      const patternLength = Array.isArray(payload.value) ? payload.value.length : 0;
      this.setEndpointMetadata('index', {
        min: 0,
        max: Math.max(0, patternLength - 1),
        step: 1
      });
    } else if (endpointId === 'index') {
      // Index changed via slider/tweak - emit event for the new position
      const idx = payload.value;
      if (this.pattern?.length && idx >= 0 && idx < this.pattern.length) {
        this.events.event.emit({
          ...payload,
          index: idx,
          iteration: this.params.iteration.value,
          value: this.resolveEventValue(idx),
          duration: this.calculateDuration(payload)
        });
      }
    }
  }

  get iterationMeta() {
    return this.getMetadata('iteration')
  }


  // ─────────────────────────────────────────────────────────────
  // Step Control
  // ─────────────────────────────────────────────────────────────


  bumpIteration(metadata: Payload) {
    let nextIteration = this.params.iteration.value + 1;
    if (nextIteration > this.iterationMeta.max) {
      nextIteration = this.iterationMeta.min;
    }
    this.params.iteration.set({
      value: nextIteration,
      time: metadata.time,
      rate: metadata.rate,
      duration: this.calculateDuration(metadata)
    });
  }



  next(e: Payload): void {
    if (!this.pattern?.length) return;

    // Read current index (evaluates getter if useExp, otherwise stored value)
    let currentIndex = Math.floor(this.params.index.value);
    currentIndex = Math.max(this.start, Math.min(currentIndex, this.end - 1));

    // Emit at current position
    const duration = this.calculateDuration(e);
    this.events.event.emit({
      ...e,
      index: currentIndex,
      iteration: this.params.iteration.value,
      value: this.resolveEventValue(currentIndex),
      duration
    });

    // Advance stored index for next step
    const direction = this.params.direction.value;
    const inc = direction === 'forwards' ? 1 : -1;
    let nextStored = currentIndex + inc;
    if (nextStored >= this.end) {
      nextStored = this.start;
      this.bumpIteration(e);
    } else if (nextStored < this.start) {
      nextStored = this.end - 1;
      this.bumpIteration(e);
    }
    this.params.index.set({
      value: nextStored,
      time: e.time,
      rate: e.rate,
      duration
    });

    if (this.sourceNode?.setHighlightStep) this.sourceNode.setHighlightStep(currentIndex);
  }


  setIndex(val: number, metadata: Payload) {
    const duration = this.calculateDuration(metadata);

    // Set index with full metadata
    this.params.index.set({
      value: val,
      time: metadata.time,
      rate: metadata.rate,
      duration
    });

    // Always emit event when index changes, regardless of transport state
    this.events.event.emit({
      ...metadata,
      index: val,
      iteration: this.params.iteration.value,
      value: this.resolveEventValue(val),
      duration
    });

    // Notify source node for UI animation
    if (this.sourceNode?.setHighlightStep) {
      this.sourceNode.setHighlightStep(val);
    }
  }

  setIteration(value: number, metadata?: Payload) {


    if (metadata) {
      this.params.iteration.set({
        value,
        time: metadata.time,
        rate: metadata.rate,
        duration: this.calculateDuration(metadata)
      });
    } else {
      this.params.iteration.set(value);
    }



  }

  reset(e: Payload): void {
    if (!this.pattern.length) return;

    const direction = this.params.direction.value;
    const resetIdx = direction === 'forwards' ? this.start : this.end - 1;

    // Reset iteration with metadata
    this.setIteration(this.iterationMeta.min, e)


    // Only emit event if index actually changes
    if (this.params.index.value !== resetIdx) {
      this.setIndex(resetIdx, e);
    }
  }

  /** Reset index and iteration without emitting (for transport start) */
  private resetIndex(): void {
    if (!this.pattern.length) return;

    const direction = this.params.direction.value;
    const resetIdx = direction === 'forwards' ? this.start : this.end - 1;

    this.params.index.set(resetIdx);
    this.setIteration(this.iterationMeta.min);
  }





  // ─────────────────────────────────────────────────────────────
  // Computed Properties
  // ─────────────────────────────────────────────────────────────

  private get start(): number {
    return this.params.cropStart.value;
  }

  private get end(): number {
    if (!this.pattern.length) return 0;
    const cropLength = this.params.cropLength.value;
    const end = cropLength > 0
      ? this.start + cropLength
      : this.pattern.length;
    return Math.min(end, this.pattern.length);
  }



  /** Resolve event value: if event has a getter (useExp), evaluate it; otherwise use pattern[idx] */
  private resolveEventValue(idx: number): any {
    if (this.deps.endpointApi.hasGetter('event')) {
      return this.deps.endpointApi.getValue('event');
    }
    return this.pattern[idx];
  }

  private calculateDuration(event: Payload): number | undefined {
    if (!event.rate) {
      this.logTrace(`calculateDuration: no currentRate, returning undefined`);
      return undefined;
    }

    const lengthRatio = this.params.lengthRatio.value;
    const durationTicks = Math.max(1, Math.round(event.rate * lengthRatio));

    this.logTrace(`calculateDuration: rate=${event.rate}, lengthRatio=${lengthRatio}, duration=${durationTicks}`);

    return durationTicks;
  }

  // ─────────────────────────────────────────────────────────────
  // Public API
  // ─────────────────────────────────────────────────────────────

  setSourceNode(node: any): void {
    this.sourceNode = node;
  }
}

// ─────────────────────────────────────────────────────────────
// Node Definition
// ─────────────────────────────────────────────────────────────

export const patternPlayerNodeDef: NodeDef<PatternPlayerNode> = {
  type: 'PatternPlayer',
  info: 'Steps through a pattern array, outputting events. Triggered by Clock or manual advance.',
  domain: Domain.Data,
  shortName: 'pat',
  endpoints,
  ctor: PatternPlayerNode
};
